import { Link, router } from "@inertiajs/react"
import { CheckCheck, PhoneCall } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CandidateReviewCard } from "@/components/candidates/candidate-review-card"
import { EmptyState } from "@/components/empty-state"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import { Button } from "@/components/ui/button"
import { candidatesPath } from "@/routes"
import type { CandidatesToReviewSection as SectionData } from "@/types/home-triage"

/**
 * OKS-953 — Brand/Home § « À contacter » (segment 3).
 *
 * `approved` candidates nobody has reached out to yet. Reuses the shipped
 * {@link HomeTriageSection} shell and {@link CandidateReviewCard} verbatim.
 *
 * **Why this one IS a status list, unlike its two new siblings.** Membership
 * here is exactly `status == "approved"`, so it rides the shipped
 * `BrandOps::CandidatesToReviewQuery` with a `statuses:` argument rather than
 * earning a query class. Segments 4 and 5 cannot do that — theirs are
 * cadence- and invite-derived — which is the whole reason they have their own
 * services.
 *
 * **The action is a LINK, not a POST.** Identical posture to « À relancer »:
 * you read the thread before you write, and the contact panel is where the
 * pitch, the channel and the prior attempts live (UX Spec §2A). Logging an
 * attempt straight from the card is deliberately not offered.
 *
 * **Cold start vs empty are different states with different copy.** Cold start
 * points at the review queue to go approve someone; empty says the outreach is
 * done. The server distinguishes them by reading the AUDIT TRAIL rather than
 * current status — a candidate approved and since contacted has left
 * `approved`, so a current-status probe would tell a caught-up brand it had
 * never started.
 */
export interface CandidatesToContactSectionProps {
  section: SectionData
}

export function CandidatesToContactSection({
  section,
}: CandidatesToContactSectionProps) {
  const { t } = useTranslation()
  const [isRetrying, setIsRetrying] = useState(false)

  function handleRetry() {
    setIsRetrying(true)
    router.reload({
      only: ["candidates_to_contact"],
      onFinish: () => setIsRetrying(false),
    })
  }

  return (
    <HomeTriageSection
      title={t("candidates.home_to_contact.title")}
      subtitle={t("candidates.home_to_contact.subtitle")}
      state={isRetrying ? "loading" : section.state}
      count={section.count}
      viewAllUrl={section.view_all_url}
      onRetry={handleRetry}
      isRetrying={isRetrying}
      testId="home-to-contact"
      // OKS-953 (PRD R7) — one of the FIVE candidate segments that collapse.
      collapseWhenEmpty
      emptyTestId="home-to-contact-empty"
      renderColdStart={() => (
        <EmptyState
          icon={PhoneCall}
          title={t("candidates.home_to_contact.cold_start.title")}
          titleAs="h2"
          description={t("candidates.home_to_contact.cold_start.description")}
          data-testid="home-to-contact-cold-start"
          actions={
            <Button asChild variant="outline">
              {/* Points at the queue that produces `approved` candidates. */}
              <Link href={candidatesPath({ status: "reviewed" })}>
                {t("candidates.home_to_contact.cold_start.cta")}
              </Link>
            </Button>
          }
        />
      )}
      renderEmpty={() => (
        <EmptyState
          icon={CheckCheck}
          title={t("candidates.home_to_contact.empty.title")}
          titleAs="h2"
          description={t("candidates.home_to_contact.empty.description")}
          data-testid="home-to-contact-empty"
        />
      )}
    >
      {section.items.map((item) => (
        <CandidateReviewCard key={item.id} item={item} />
      ))}
    </HomeTriageSection>
  )
}
